import { batch } from 'react-redux';
import template from 'url-template';
import axios from 'axios';

import { get, isEmpty } from 'lodash';

import awsHeader from '../helpers/awsAuth';
import { buildInventoryMap } from '../helpers/inventoryList';
import { buildTree } from '../helpers/bacnetInventoryList';
import { getErrorMessage } from '../helpers/errors';
import k2aBaseApi from '../apis/key2act';
import {
    enqueueSnackbar,
    handleNetworkError
} from './applicationMessageActions';

import { fetchDevices } from './deviceActions';

import {
    FETCH_BACNET_INVENTORY,
    FETCH_INVENTORY,
    INVENTORY_LOAD_BEGIN,
    INVENTORY_LOAD_END,
    INVENTORY_LOAD_ERROR,
    INVENTORY_RESET,
    INVENTORY_TOGGLE_SELECT,
    INVENTORY_UPDATE
} from '../actions/actionTypes';

const InventoryScanUrl = template.parse('/basagents/{agentId}/inventory-scan{?configurationId,startRange,endRange,baseUrl,partialUrl,inventoryScanStatus}');

export const clearInventory = () => ({
    type:    FETCH_INVENTORY,
    payload: {
        inventory: {}
    }
});

export const clearBacnetInventory = () => ({
    type:    FETCH_BACNET_INVENTORY,
    payload: {
        bacnetInventory: []
    }
});

export const fetchBacnetInventory = (agent) => async (dispatch, getState) => {
    const header = await awsHeader();

    const { bacnetInventory, lastRequestId } = getState().inventory;
    const agentId = get(agent, 'id', '');
    const configurationId = get(agent, 'driverConfiguration[0].id', '');
    const requestId = `${agentId}::${configurationId}`;

    if (!isEmpty(bacnetInventory) && lastRequestId === requestId) {
        return bacnetInventory;
    }

    dispatch({
        type:    FETCH_INVENTORY,
        payload: {
            bacnetInventory: null,
            lastRequestId:   requestId
        }
    });

    try {
        dispatch(beginLoading());
        const response = await k2aBaseApi.get(
            InventoryScanUrl.expand({
                agentId,
                configurationId
            }),
            header
        );

        const inventoryUrl = get(response, 'data.inventoryPath', '');

        let payload;

        if (!inventoryUrl.length) {
            payload = [];
        } else {
            payload = await axios.get(inventoryUrl);
        }

        const bacnetInventory = buildTree(get(payload, 'data', []));

        const companyInfo = getState().companyInfo;

        let devices = getState().device.devices;

        if (isEmpty(devices)) {
            await dispatch(fetchDevices(companyInfo.companyId, agentId, configurationId, agent.driverConfiguration[0].basType));

            devices = getState().device.devices;
        }

        devices.forEach(device => {
            const targetInventory = bacnetInventory.find(inventory => inventory.DeviceName === device.name);
            if (targetInventory) {
                targetInventory.isCreated = true;
                targetInventory.profileName = device.profileName;
                targetInventory.profileId = device.basProfileId;
            }
        });

        dispatch({
            type:    FETCH_INVENTORY,
            payload: {
                bacnetInventory,
                lastRequestId: requestId
            }
        });

        return bacnetInventory;
    } catch (error) {
        batch(() => {
            dispatch(errorLoading(getErrorMessage(error)));
            dispatch(handleNetworkError(error));
        });
    } finally {
        dispatch(endLoading());
    }
};

export const fetchInventory = (agent) => async (dispatch, getState) => {
    const { inventory, lastRequestId } = getState().inventory;
    const agentId = agent.id;
    const configurationId = agent.driverConfiguration[0].id;
    const requestId = `${agentId}::${configurationId}`;

    if (!isEmpty(inventory) && lastRequestId === requestId) {
        let devices = getState().device.devices;

        devices.forEach(device => {
            if (device.oBIXPath) {
                const targetInventory = inventory[device.oBIXPath];

                if (targetInventory) {
                    targetInventory.isCreated = true;
                    targetInventory.profileName = device.profileName;
                    targetInventory.profileId = device.basProfileId;
                }
            }
        });
        return inventory;
    }

    dispatch({
        type:    FETCH_INVENTORY,
        payload: {
            inventory:     null,
            lastRequestId: requestId
        }
    });

    const header = await awsHeader();

    try {
        dispatch(beginLoading());
        const response = await k2aBaseApi.get(
            InventoryScanUrl.expand({
                agentId,
                configurationId
            }),
            header
        );

        const inventoryUrl = get(response, 'data.inventoryPath', '');

        let payload;

        if (!inventoryUrl.length) {
            payload = [];
        } else {
            payload = await axios.get(inventoryUrl);
        }

        const inventory = buildInventoryMap(get(payload, 'data', []));

        const companyInfo = getState().companyInfo;

        let devices = getState().device.devices;

        if (isEmpty(devices)) {
            await dispatch(fetchDevices(companyInfo.companyId, agentId, configurationId, agent.driverConfiguration[0].basType));

            devices = getState().device.devices;
        }

        devices.forEach(device => {
            if (device.oBIXPath) {
                const targetInventory = inventory[device.oBIXPath];

                if (targetInventory) {
                    targetInventory.isCreated = true;
                    targetInventory.profileName = device.profileName;
                    targetInventory.profileId = device.basProfileId;
                }
            }
        });

        dispatch({
            type:    FETCH_INVENTORY,
            payload: {
                inventory,
                lastRequestId: requestId
            }
        });

        return inventory;
    } catch (error) {
        batch(() => {
            dispatch(errorLoading(getErrorMessage(error)));
            dispatch(handleNetworkError(error));
        });
        return false;
    } finally {
        dispatch(endLoading());
    }
};

export const selectInventory = payload => ({
    type: INVENTORY_TOGGLE_SELECT,
    payload
});

export const setBacnetInventory = payload => ({
    type:    FETCH_BACNET_INVENTORY,
    payload: {
        bacnetInventory: payload
    }
});

export const triggerInventoryScan = (agentId, configurationId, dateRange = {}, path = {},  isBackgroundScan = false) => async (dispatch) => {
    const header = await awsHeader();

    dispatch({
        type:    INVENTORY_RESET,
        payload: {}
    });

    const startRange = dateRange.startRange;
    const endRange = dateRange.endRange;

    const baseUrl = path.basePath;
    const partialUrl = path.partialPath;

    try {
        dispatch(beginLoading());
        await k2aBaseApi.post(
            InventoryScanUrl.expand({
                agentId,
                configurationId,
                startRange,
                endRange,
                baseUrl,
                partialUrl
            }),
            {},
            header
        );
        if (!isBackgroundScan) {
            dispatch(enqueueSnackbar('Inventory refresh initiated.', 'success'));
        }
        
    } catch (error) {
        batch(() => {
            dispatch(errorLoading(getErrorMessage(error)));
            dispatch(handleNetworkError(error));
        });
    } finally {
        dispatch(endLoading());
    }
};

export const stopInventoryScan = (agentId, configurationId) => async (dispatch) => {
    const header = await awsHeader();
    const inventoryScanStatus = true;

    try {
        dispatch(beginLoading());
        await k2aBaseApi.post(
            InventoryScanUrl.expand({
                agentId,
                configurationId,
                inventoryScanStatus
            }),
            {},
            header
        );

        dispatch(enqueueSnackbar('Inventory Scan Stopped'));
    } catch (error) {
        batch(() => {
            dispatch(errorLoading(getErrorMessage(error)));
            dispatch(handleNetworkError(error));
        });
    } finally {
        dispatch(endLoading());
    }
};

export const updateInventory = (id, patch) => {
    return {
        type:    INVENTORY_UPDATE,
        payload: {
            id,
            patch
        }
    };
};

const beginLoading = () => ({
    type:    INVENTORY_LOAD_BEGIN,
    payload: null
});

const endLoading = () => ({
    type:    INVENTORY_LOAD_END,
    payload: null
});

const errorLoading = (error) => ({
    type:    INVENTORY_LOAD_ERROR,
    payload: error
});
